/**
 * Bridges React Query loading/error states to the global AppContext overlay.
 *
 * Only shows the loading overlay on **initial load** (no cached data yet).
 * Background refetches (stale data exists) are silent — the user sees
 * the existing data while fresh data loads behind the scenes.
 *
 * Tracks ownership: only clears loading if the bridge set it, so manual
 * loading messages (e.g., "Submitting signature...") are not overwritten
 * by a background refetch completing.
 *
 * Cleans up on unmount — if this bridge owns the loading overlay when the
 * component unmounts (e.g., route change during load), it clears the overlay
 * so it doesn't get stuck indefinitely.
 */

import { useEffect, useContext, useRef } from 'react';
import AppContext from '../store/app-context';

export function useQueryBridge(
  isLoading: boolean,
  error: Error | null,
  loadingMessage: string,
  errorPrefix = 'Failed to load',
  /** When true, data exists in cache — suppress loading overlay during background refetch */
  hasData = false
) {
  const appCtx = useContext(AppContext);
  const bridgeOwnsLoading = useRef(false);
  const newLoadingRef = useRef(appCtx.newLoading);
  newLoadingRef.current = appCtx.newLoading;

  useEffect(() => {
    const showLoading = isLoading && !hasData;
    if (showLoading) {
      bridgeOwnsLoading.current = true;
      appCtx.newLoading(loadingMessage);
    } else if (bridgeOwnsLoading.current) {
      // Only clear loading if the bridge was the one that set it
      bridgeOwnsLoading.current = false;
      appCtx.newLoading('');
    }

    // Cleanup: if this component unmounts while it owns the overlay, clear it
    return () => {
      if (bridgeOwnsLoading.current) {
        bridgeOwnsLoading.current = false;
        newLoadingRef.current('');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasData]);

  useEffect(() => {
    if (error) {
      if (bridgeOwnsLoading.current) {
        bridgeOwnsLoading.current = false;
        appCtx.newLoading('');
      }
      appCtx.newMessage({ text: error.message || errorPrefix, type: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
}
